// AN UPLOADER RULE (Rules → Companies → Uploaders): one address or `@domain`,
// and the three facts about it that live in three tables — may it email
// documents into intake (`intake_senders`), which company its documents file to
// (`company_email_domains`), and whether their format is set rather than
// guessed at extraction (`sender_format_defaults`, D-379). An exact address wins over
// its domain for routing and for format. `PUT /sender-rules` writes every side
// of one rule in ONE transaction (code review G-01, D-275).
//
// One rule here, used by the route that reactivates a rule and by the screen
// that shows what an Inactive rule would do when made Active again, so the two
// cannot disagree.

import type { DocumentForm } from './enums.js'

/** The fields of either table's row this rule reads. */
interface SenderRuleSide {
  is_active: boolean
  retired_at?: string | null
}

function retiredMs(row: SenderRuleSide | null): number {
  if (row === null || !row.retired_at) return Number.NaN
  return Date.parse(row.retired_at)
}

/** The most recently made-Inactive row of one side. A row with no
    `retired_at` counts as oldest. */
function latestInactive<T extends SenderRuleSide>(rows: readonly T[]): T | null {
  let best: T | null = null
  for (const row of rows) {
    if (row.is_active) continue
    if (best === null) {
      best = row
      continue
    }
    const a = retiredMs(row)
    const b = retiredMs(best)
    if (Number.isNaN(b) || (!Number.isNaN(a) && a >= b)) best = row
  }
  return best
}

/**
 * THE ROWS AN INACTIVE RULE HELD WHEN IT WENT INACTIVE — what making it Active
 * puts back. `null` on every side while any side has an Active row.
 *
 * On each side, the most recently made-Inactive row, kept only if it went
 * Inactive IN THE SAME WRITE as the latest of the others: the same
 * `retired_at`, exactly. `PUT /sender-rules` makes every row Inactive in one
 * transaction, and Postgres stamps each with the transaction's own `now()`, so
 * the values are equal by construction. A side switched off by an earlier edit
 * carries an earlier stamp and is not switched back on. (This replaced a
 * 60-second window that guessed at the same thing from separate writes.)
 *
 * A side with no stamp at all is kept only when no side has one — there is
 * then nothing to tell them apart by.
 */
export function dormantSenderRows<
  I extends SenderRuleSide,
  R extends SenderRuleSide,
  F extends SenderRuleSide = SenderRuleSide,
  B extends SenderRuleSide = SenderRuleSide,
>(
  intake: readonly I[],
  routing: readonly R[],
  format: readonly F[] = [],
  inApp: readonly B[] = [],
): { intake: I | null; routing: R | null; format: F | null; inApp: B | null } {
  if ([...intake, ...routing, ...format, ...inApp].some((row) => row.is_active)) {
    return { intake: null, routing: null, format: null, inApp: null }
  }
  const i = latestInactive(intake)
  const r = latestInactive(routing)
  const f = latestInactive(format)
  const b = latestInactive(inApp)
  const stamps = [retiredMs(i), retiredMs(r), retiredMs(f), retiredMs(b)]
  if (stamps.every(Number.isNaN)) return { intake: i, routing: r, format: f, inApp: b }
  const latest = Math.max(...stamps.filter((t) => !Number.isNaN(t)))
  const keep = <T>(row: T | null, t: number): T | null => (t === latest ? row : null)
  return {
    intake: keep(i, stamps[0] as number),
    routing: keep(r, stamps[1] as number),
    format: keep(f, stamps[2] as number),
    inApp: keep(b, stamps[3] as number),
  }
}

/**
 * WHO A RULE IS ABOUT, the first choice of its sentence (owner, 2026-10-19 —
 * D-379): "a specific user" is one full address, "a general domain" is a whole
 * `@domain`.
 */
export type SenderKeyKind = 'address' | 'domain'

/** The kind a stored key is: an `@domain` or one address. */
export function senderKeyKind(key: string): SenderKeyKind {
  return key.startsWith('@') ? 'domain' : 'address'
}

/**
 * Whether a stored key (`normalizeSenderKey`'s answer) is what its kind says:
 * one address `name@example.com`, or one domain `@example.com`. The route
 * refuses a key that is not, and the editor says so before Save.
 */
export function senderKeyFits(kind: SenderKeyKind, key: string): boolean {
  if (key.length > 254) return false
  return kind === 'address' ? /^[^@\s]+@[^@\s]+$/.test(key) : /^@[^@\s]+$/.test(key)
}

/** The refusal for a key that is not what its kind says, as the route and the editor both word it. */
export function senderKeyMisfit(kind: SenderKeyKind): string {
  return kind === 'address'
    ? 'Enter one full address, like name@example.com.'
    : 'Enter one domain, like @example.com.'
}

/**
 * THE KEY A SENDER RULE IS STORED UNDER: trimmed and lowercased, and a bare
 * `example.com` gains its `@`. One copy for the route that stores it and the
 * editor that says "Will be stored as …" (QA 2026-10-04 A-01). Blank stays blank.
 */
export function normalizeSenderKey(raw: string): string {
  const t = raw.trim().toLowerCase()
  if (t === '' || t.includes('@')) return t
  return `@${t}`
}

/**
 * The refusal for a second Active rule on one key, worded as Vendors and
 * Aliases word theirs. The route sends it as `duplicate_key`; the editor shows
 * the same sentence before Save (QA 2026-10-04 A-02).
 */
export function senderRuleTaken(key: string): string {
  return `Already in use: “${key}” already has an Active rule. Edit that rule rather than adding a second one.`
}

/**
 * The keys an upload's sender is matched on, most specific first: the address
 * itself, then its `@domain`. Empty for a source that is not an address.
 */
export function senderRuleKeys(source: string | null | undefined): string[] {
  const address = normalizeSenderKey(source ?? '')
  const at = address.lastIndexOf('@')
  if (at <= 0 || at === address.length - 1) return []
  return [address, address.slice(at)]
}

/**
 * The format an uploader rule sets for every invoice of an upload (D-379), or
 * null when no Active rule does and pass 1's guess stands ("is interpreted",
 * D-378). THE EXACT ADDRESS WINS over its domain: a scan-to-email mailbox on a
 * domain whose people send digital bills.
 */
export function senderDocumentForm(
  source: string | null | undefined,
  rows: readonly { key: string; document_form: DocumentForm }[],
): DocumentForm | null {
  for (const key of senderRuleKeys(source)) {
    const row = rows.find((r) => r.key === key)
    if (row !== undefined) return row.document_form
  }
  return null
}

/**
 * MAY THIS PERSON UPLOAD IN THE APP (owner, 2026-10-22 — D-394)? Their own
 * address decides when it has a rule of its own (any Active side); otherwise
 * their domain's rule does; with neither, no rule stands in their way. This is
 * the RULE's half only: the upload routes and the profile also require a role
 * or an appointment (`hasAnyStanding`). `ruledKeys` are the keys with any Active rule side, `blockedKeys`
 * those whose rule says in-app uploads are not allowed. One rule for the
 * upload routes and the profile, so the button and the refusal agree.
 */
export function inAppUploadAllowed(
  email: string | null | undefined,
  ruledKeys: ReadonlySet<string>,
  blockedKeys: ReadonlySet<string>,
): boolean {
  for (const key of senderRuleKeys(email)) {
    if (blockedKeys.has(key)) return false
    if (ruledKeys.has(key)) return true
  }
  return true
}
